import { Link } from 'react-router-dom';
import { useReveal } from '../lib/anim/useReveal';
import { usePageMeta } from '../lib/usePageMeta';

const RAILS = [
  { id: 1, label: 'Self-serve', body: 'Anyone with a credit card can buy. No demo call, no quote request, no waiting.' },
  { id: 2, label: 'No human in the loop', body: 'No demos, no calls. The product answers the question end-to-end on its own.' },
  { id: 3, label: 'Ad to answer in under 10 minutes', body: 'Five is better. If it takes longer, the ICP is wrong or the product is broken.' },
  { id: 4, label: 'Low ticket', body: '$9–$99 one-off or $29–$299/mo. Nothing that requires a procurement sign-off.' },
  { id: 5, label: 'No consulting upsell inside the product', body: 'Track A is self-contained. Upsells live outside — not as a crutch inside the product.' },
  { id: 6, label: 'The cash math has to plausibly close', body: 'If the CFA gate can\'t close at this price with realistic ad spend, it\'s not a product.' },
];

const CADENCE_STEPS = [
  'Brainstorm',
  'Spec',
  'Plan',
  'Build',
  'Review',
  'Ship',
  'Measure',
];

const WONT_DO = [
  'No free tier (rails require self-serve credit card)',
  'No staging environments (prod-only with CF Pages previews disabled)',
  'No agency engagements badged as products',
  'No human-in-the-loop on Track A',
  'No data movement across tenant boundaries (RLS-enforced)',
];

function SectionRule() {
  return <hr style={{ borderColor: 'var(--color-hairline)', margin: '56px 0' }} />;
}

export function Playbook() {
  usePageMeta(
    'Operating Playbook — Clarity Platform',
    'How Onwards builds, ships, and decides what to keep. The 50/30/10 model, six rails, CFA gate, and operating cadence — all public.',
  );
  const intro = useReveal();
  const s1 = useReveal({ delay: 1 });
  const s2 = useReveal({ delay: 1 });
  const s3 = useReveal({ delay: 1 });
  const s4 = useReveal({ delay: 1 });
  const s5 = useReveal({ delay: 1 });
  const s6 = useReveal({ delay: 1 });
  const footer = useReveal();

  return (
    <main className="min-h-screen text-ink" style={{ background: 'var(--color-bg)' }}>
      {/* ── Hero ── */}
      <section className="relative overflow-hidden py-28 md:py-36">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-16 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full blur-3xl"
          style={{ background: 'var(--color-accent-soft)' }}
        />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <p className="text-sm uppercase tracking-widest mb-5 font-medium" style={{ color: 'var(--color-accent)', fontFamily: 'var(--font-mono)' }}>
            Transparency
          </p>
          <div ref={intro.ref} {...intro.props} className={intro.props.className}>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight tracking-tight text-ink" style={{ fontFamily: 'var(--font-display)' }}>
              Onwards&rsquo; operating playbook
            </h1>
            <p className="mt-5 text-xl md:text-2xl font-normal" style={{ color: 'var(--color-mute)' }}>
              How we build, ship, and decide what to keep. Updated as we learn.
            </p>
          </div>
        </div>
      </section>

      {/* ── Body ── */}
      <div className="max-w-4xl mx-auto px-6 pb-8">

        {/* Section 1 — 50/30/10 */}
        <section aria-labelledby="s-model">
          <div ref={s1.ref} {...s1.props} className={s1.props.className}>
            <h2 id="s-model" className="text-2xl md:text-3xl font-bold text-ink mb-6" style={{ fontFamily: 'var(--font-display)' }}>
              The 50/30/10 model
            </h2>
            <p className="leading-relaxed mb-8" style={{ color: 'var(--color-mute)' }}>
              Every Onwards micro-SaaS is built on three layers, sized by the effort they take to build.
            </p>
            <div className="space-y-6">
              <div className="rounded-xl p-6" style={{ border: '1px solid var(--color-hairline)', background: 'var(--color-surface)' }}>
                <p className="font-semibold text-sm uppercase tracking-widest mb-2" style={{ color: 'var(--color-accent)', fontFamily: 'var(--font-mono)' }}>Chassis — 50%+</p>
                <p className="font-medium mb-2 text-ink">Built once. Never built again.</p>
                <p className="leading-relaxed" style={{ color: 'var(--color-mute)' }}>
                  CF Pages + Workers + Supabase Sydney + Stripe + Resend + Workers AI + Vectorize.
                  The plumbing is solved. Every new product inherits it.
                </p>
              </div>
              <div className="rounded-xl p-6" style={{ border: '1px solid var(--color-hairline)', background: 'var(--color-surface)' }}>
                <p className="font-semibold text-sm uppercase tracking-widest mb-2" style={{ color: 'var(--color-accent)', fontFamily: 'var(--font-mono)' }}>Engine — 30–40%</p>
                <p className="font-medium mb-2 text-ink">The product-specific capability. Reusable across products with the same shape.</p>
                <p className="leading-relaxed" style={{ color: 'var(--color-mute)' }}>
                  Current engines: Nexlytics (file parser + scorer + RAG), Mockingbird (vision blueprint + brand swap),
                  Pulse Check (multi-tenant scorecard), Glide (specialist CV), Mobipet (mobile + SMS), AI-CTO (RAG advisor).
                </p>
              </div>
              <div className="rounded-xl p-6" style={{ border: '1px solid var(--color-hairline)', background: 'var(--color-surface)' }}>
                <p className="font-semibold text-sm uppercase tracking-widest mb-2" style={{ color: 'var(--color-accent)', fontFamily: 'var(--font-mono)' }}>Use-case wrap — 10%</p>
                <p className="font-medium mb-2 text-ink">Days or weeks of work, not months.</p>
                <p className="leading-relaxed" style={{ color: 'var(--color-mute)' }}>
                  ICP &times; verdict-format &times; price &times; channel. The only part that changes between products.
                </p>
              </div>
            </div>
          </div>
        </section>

        <SectionRule />

        {/* Section 2 — Six rails */}
        <section aria-labelledby="s-rails">
          <div ref={s2.ref} {...s2.props} className={s2.props.className}>
            <h2 id="s-rails" className="text-2xl md:text-3xl font-bold text-ink mb-6" style={{ fontFamily: 'var(--font-display)' }}>
              The six rails
            </h2>
            <p className="leading-relaxed mb-8" style={{ color: 'var(--color-mute)' }}>
              A product must fit all six to be an Onwards micro-SaaS. Any miss is a kill signal, not a design challenge.
            </p>
            <ol className="space-y-4">
              {RAILS.map((rail) => (
                <li key={rail.id} className="flex gap-4">
                  <span
                    className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm"
                    style={{ background: 'var(--color-accent-soft)', border: '1px solid rgba(79,70,229,0.25)', color: 'var(--color-accent)' }}
                  >
                    {rail.id}
                  </span>
                  <div>
                    <p className="font-medium text-ink">{rail.label}</p>
                    <p className="text-sm mt-1 leading-relaxed" style={{ color: 'var(--color-mute)' }}>{rail.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <SectionRule />

        {/* Section 3 — CFA gate */}
        <section aria-labelledby="s-cfa">
          <div ref={s3.ref} {...s3.props} className={s3.props.className}>
            <h2 id="s-cfa" className="text-2xl md:text-3xl font-bold text-ink mb-6" style={{ fontFamily: 'var(--font-display)' }}>
              The CFA gate
            </h2>
            <p className="leading-relaxed mb-8" style={{ color: 'var(--color-mute)' }}>
              Every product gets a 30-day Hormozi CFA gate. If it doesn&rsquo;t pass, we kill it — not pivot it into a consulting engagement.
            </p>
            <div className="rounded-xl p-6 space-y-4" style={{ border: '1px solid rgba(79,70,229,0.25)', background: 'var(--color-accent-soft)' }}>
              {[
                '$5k/mo into ads → $10k/mo positive cash flow on top',
                '~$15k revenue, ~$5k spend, ~$10k clean',
                'Five cash-positive products = $50k/mo cash machine',
                'This is the qualifier for whether any 10% bet is worth pursuing',
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <span style={{ color: 'var(--color-accent)' }} className="mt-0.5">→</span>
                  <p style={{ color: 'var(--color-ink)' }}>{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <SectionRule />

        {/* Section 4 — Operating cadence */}
        <section aria-labelledby="s-cadence">
          <div ref={s4.ref} {...s4.props} className={s4.props.className}>
            <h2 id="s-cadence" className="text-2xl md:text-3xl font-bold text-ink mb-6" style={{ fontFamily: 'var(--font-display)' }}>
              Operating cadence
            </h2>
            <div className="flex flex-wrap gap-2 mb-8">
              {CADENCE_STEPS.map((step, i) => (
                <span key={step} className="flex items-center gap-2">
                  <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-hairline)', color: 'var(--color-ink)' }}>
                    {step}
                  </span>
                  {i < CADENCE_STEPS.length - 1 && (
                    <span className="text-sm" style={{ color: 'var(--color-mute)' }}>&rarr;</span>
                  )}
                </span>
              ))}
            </div>
            <ul className="space-y-3">
              {[
                'Every spec is a paperclip context doc with explicit kill criteria',
                'Every plan is task-decomposed with TDD and tested gates',
                'Every ship is a public changelog entry within 24 hours',
                'Every product gets a 30-day Hormozi CFA gate; if it fails, kill it',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3" style={{ color: 'var(--color-mute)' }}>
                  <span style={{ color: 'var(--color-accent)', flexShrink: 0 }} className="mt-0.5">&#8250;</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <SectionRule />

        {/* Section 5 — Ships in days */}
        <section aria-labelledby="s-speed">
          <div ref={s5.ref} {...s5.props} className={s5.props.className}>
            <h2 id="s-speed" className="text-2xl md:text-3xl font-bold text-ink mb-6" style={{ fontFamily: 'var(--font-display)' }}>
              How Onwards ships in days, not months
            </h2>
            <ul className="space-y-3">
              {[
                'Reusing the chassis means new products skip the infra grind',
                'AI subagents handle scaffolding, tests, deploy; humans handle judgment + voice',
                'Decisions are stored as memory entries with why + how-to-apply; not re-litigated',
                'Cross-tenant data isolation is solved once at the chassis layer and inherited',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3" style={{ color: 'var(--color-mute)' }}>
                  <span style={{ color: 'var(--color-accent)', flexShrink: 0 }} className="mt-0.5">&#8250;</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <SectionRule />

        {/* Section 6 — What we won't do */}
        <section aria-labelledby="s-wont">
          <div ref={s6.ref} {...s6.props} className={s6.props.className}>
            <h2 id="s-wont" className="text-2xl md:text-3xl font-bold text-ink mb-6" style={{ fontFamily: 'var(--font-display)' }}>
              What we won&rsquo;t do
            </h2>
            <ul className="space-y-3">
              {WONT_DO.map((item) => (
                <li key={item} className="flex items-start gap-3" style={{ color: 'var(--color-mute)' }}>
                  <span className="text-red-400/70 mt-0.5 flex-shrink-0">&#10007;</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <SectionRule />

        {/* Footer CTA */}
        <section className="pb-16">
          <div
            ref={footer.ref}
            {...footer.props}
            className={`text-center ${footer.props.className}`}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-ink mb-4" style={{ fontFamily: 'var(--font-display)' }}>
              Ready to build on these rails?
            </h2>
            <p className="leading-relaxed mb-8 max-w-xl mx-auto" style={{ color: 'var(--color-mute)' }}>
              Pick an engine and ship your first product on the chassis that&rsquo;s already built.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/engines"
                className="inline-flex items-center justify-center px-8 py-4 rounded font-medium text-base transition-colors"
                style={{ border: '1px solid var(--color-hairline)', color: 'var(--color-ink)' }}
              >
                Pick an engine &rarr;
              </Link>
              <Link
                to="/signup?tier=pilot"
                className="inline-flex items-center justify-center px-8 py-4 rounded font-semibold text-base hover:opacity-90 transition-opacity"
                style={{ background: 'var(--color-accent)', color: '#FFFFFF' }}
              >
                Start with Pilot &mdash; $149/mo
              </Link>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
